import React, { useState, useEffect } from 'react';
import { getTicketTags, createTicketTag, setTicketTags, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';

/** Etichetta colorata: il colore è quello scelto per l'etichetta (sfondo leggero, testo e bordo dello stesso colore). */
export function TagChip({ tag, onRemove, onClick }) {
  const style = { '--tg': tag.color || '#6366f1' };
  return (
    <span className={'tg-chip' + (onClick ? ' tg-chip-link' : '')} style={style} onClick={onClick}>
      <span>{tag.name}</span>
      {onRemove && (
        <button type="button" className="tg-chip-x" onClick={(e) => { e.stopPropagation(); onRemove(tag); }} aria-label={tag.name}>×</button>
      )}
    </span>
  );
}

/** Elenco compatto di etichette (per l'elenco ticket). */
export function TagChips({ tags, onPick }) {
  if (!tags || tags.length === 0) return null;
  return (
    <span className="tg-list">
      {tags.map((tag) => <TagChip key={tag.id} tag={tag} onClick={onPick ? () => onPick(tag) : undefined} />)}
    </span>
  );
}

/**
 * Etichette di un ticket: si aggiungono scrivendo (si propongono quelle già esistenti; se non esiste, si crea) e si tolgono
 * con la ×. Ogni modifica si salva subito.
 */
function TicketTagsEditor({ ticketId, tags, onChange }) {
  const { t } = useLanguage();
  const [all, setAll] = useState([]);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getTicketTags().then((d) => setAll(d.tags || [])).catch(() => {});
  }, []);

  const save = async (next) => {
    setBusy(true);
    setError('');
    try {
      const res = await setTicketTags(ticketId, next.map((x) => x.id));
      onChange(res.tags || []);
    } catch (err) {
      setError(errorMessage(err, t('tags.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const name = text.trim();
    if (!name || busy) return;
    setText('');
    let tag = all.find((x) => x.name.toLowerCase() === name.toLowerCase());
    if (!tag) {
      try {
        tag = (await createTicketTag({ name })).tag;
        setAll((prev) => [...prev, tag]);
      } catch (err) {
        setError(errorMessage(err, t('tags.saveError')));
        return;
      }
    }
    if (tags.some((x) => x.id === tag.id)) return;
    await save([...tags, tag]);
  };

  const available = all.filter((x) => !tags.some((y) => y.id === x.id));

  return (
    <div className="tg-editor">
      <div className="tg-list">
        {tags.map((tag) => <TagChip key={tag.id} tag={tag} onRemove={() => save(tags.filter((x) => x.id !== tag.id))} />)}
      </div>
      <form onSubmit={add} className="tg-add">
        <input
          list={`tg-options-${ticketId}`}
          value={text}
          maxLength={40}
          placeholder={t('tags.addPlaceholder')}
          onChange={(e) => setText(e.target.value)}
          aria-label={t('tags.add')}
        />
        <datalist id={`tg-options-${ticketId}`}>
          {available.map((x) => <option key={x.id} value={x.name} />)}
        </datalist>
      </form>
      {error && <p className="tp-hint" style={{ color: 'var(--danger, #dc2626)' }}>{error}</p>}
    </div>
  );
}

export default TicketTagsEditor;
